import type { Cell } from "../src/protocol";

/**
 * Snaps a drag that started at `start` to the closest of the 8 valid directions.
 * `point` is the pointer position in fractional cell units ([row, col], where the
 * center of cell (r, c) is (r + 0.5, c + 0.5)). The result is clamped to the board.
 */
export function snapToLine(start: Cell, point: [number, number], size: number): Cell {
  const dy = point[0] - (start[0] + 0.5);
  const dx = point[1] - (start[1] + 0.5);
  if (Math.hypot(dx, dy) < 0.5) return start;

  const octant = Math.round(Math.atan2(dy, dx) / (Math.PI / 4));
  const dr = Math.round(Math.sin((octant * Math.PI) / 4));
  const dc = Math.round(Math.cos((octant * Math.PI) / 4));

  // Projection onto the (unnormalized) direction, divided by its step length in cells.
  let steps = Math.max(0, Math.round((dx * dc + dy * dr) / (Math.abs(dr) + Math.abs(dc))));
  while (steps > 0 && !inBounds([start[0] + dr * steps, start[1] + dc * steps], size)) steps--;
  return [start[0] + dr * steps, start[1] + dc * steps];
}

export function inBounds([r, c]: Cell, size: number): boolean {
  return r >= 0 && r < size && c >= 0 && c < size;
}

export function sameCell(a: Cell, b: Cell): boolean {
  return a[0] === b[0] && a[1] === b[1];
}
